/* eslint-disable jsx-a11y/no-autofocus */
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import Button from 'components/_core/Button';
import Card from 'components/_core/Card';
import * as FormComponents from 'components/_core/form';
import Text from 'components/_core/Text';
import Form from 'components/Form';
import Row from 'components/Row';

import BeneficiaryStatusBadge from '../BeneficiaryStatusBadge';
import locales from '../locales';
import { BeneficiaryCardComponentProps, BeneficiaryInvitation } from '../types';
import beneficiaryCardFormSchema, {
  BeneficiaryCardFormSchemaValid,
} from './formSchema';
import {
  BeneficiaryCardContentColumn,
  BeneficiaryCardTitleContainer,
  BeneficiaryCardWrapper,
} from './styles';

const BeneficiaryCard: FC<BeneficiaryCardComponentProps> = ({
  beneficiary,
  isOpen,
  loading,
  onClose,
  onOpen,
  onSubmit,
  sendInvitation,
}) => {
  const {
    firstName,
    invitation = {},
    lastName,
    onboardingStatus,
  } = beneficiary;
  const { canResendInvite, inviteeEmail: email = '' } =
    invitation as BeneficiaryInvitation;
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm({
    defaultValues: { email: beneficiary.invitation?.inviteeEmail },
    resolver: zodResolver(beneficiaryCardFormSchema),
  });

  const submitHandler = handleSubmit((values) => {
    // handleSubmit is called only if form validation is successful
    onSubmit(values as BeneficiaryCardFormSchemaValid);
  });

  const canEdit = onboardingStatus === 'NOT_STARTED';
  const hasFooter = canEdit && (!email || canResendInvite || isOpen);

  return (
    <BeneficiaryCardWrapper>
      <Form onValidSubmit={submitHandler}>
        <Card.Group>
          <Card>
            <Row>
              <BeneficiaryCardContentColumn>
                <BeneficiaryCardTitleContainer>
                  <Text fontWeight="weight-500" size="xs" variant="primary">
                    {`${firstName} ${lastName}`}
                  </Text>
                </BeneficiaryCardTitleContainer>
                {isOpen ? null : (
                  <Text size="sm">
                    {!email ? locales.statusEmailMissing : email}
                  </Text>
                )}
              </BeneficiaryCardContentColumn>
              <BeneficiaryStatusBadge
                invitation={invitation as BeneficiaryInvitation}
                onboardingStatus={onboardingStatus}
              />
            </Row>
            <Box>
              {!isOpen && canEdit ? (
                <Button onClick={onOpen} size="sm" variant="inline-primary">
                  {email ? locales.editEmail : locales.addEmail}
                </Button>
              ) : null}
              {isOpen ? (
                <FormComponents.Field
                  error={errors.email?.message}
                  label={locales.inputEmailLabel}
                  marginTop="space-16"
                >
                  <FormComponents.Input
                    autoFocus
                    disabled={loading}
                    type="email"
                    {...register('email')}
                  />
                </FormComponents.Field>
              ) : null}
            </Box>
          </Card>
          {hasFooter ? (
            <Card>
              {isOpen ? (
                <Row justifyContent="space-between">
                  <Button
                    display="flex"
                    isDisabled={loading}
                    justifyContent="center"
                    onClick={onClose}
                    variant="inline-secondary"
                  >
                    {locales.cancel}
                  </Button>
                  <Button
                    icon="send"
                    isDisabled={loading}
                    type="submit"
                    variant="secondary"
                  >
                    {!email ? locales.invite : locales.resend}
                  </Button>
                </Row>
              ) : (
                <Row justifyContent="center">
                  {email ? (
                    <Button
                      icon="send"
                      isDisabled={!email || loading}
                      onClick={sendInvitation}
                      variant="secondary"
                    >
                      {locales.remind}
                    </Button>
                  ) : (
                    <Button
                      cursor={!email ? 'default' : 'inherit'}
                      isDisabled={!email || loading}
                      onClick={sendInvitation}
                      variant="secondary"
                    >
                      {locales.invite}
                    </Button>
                  )}
                </Row>
              )}
            </Card>
          ) : null}
        </Card.Group>
      </Form>
    </BeneficiaryCardWrapper>
  );
};

export default BeneficiaryCard;
